import { AppliedFilters } from '../../ui/AppliedFilters/AppliedFilters';
import { TextButton } from '../../ui/Button/Buttons';
import DatePicker from '../../ui/DatePicker/DatePicker';
import { FilterDropdown } from '../../ui/FilterDropdown/FilterDropdown';
import {
  RepositoryProvider,
  RepositoryStatus,
} from '../../../types/Repository.types';
import { useMemo } from 'react';
import { DateRange } from '../../ui/DatePicker/DatePicker.type';
import { Toggle } from '../../ui/Toggle/Toggle';

interface RepositoryManagementFiltersProps {
  selectedProvider: RepositoryProvider[];
  onProviderChange: (values: RepositoryProvider[]) => void;
  selectedBranch: string[];
  onBranchChange: (values: string[]) => void;
  selectedStatus: RepositoryStatus[];
  onStatusChange: (values: RepositoryStatus[]) => void;
  lastSyncedFrom: string | null;
  lastSyncedTo: string | null;
  onLastSyncedChange: (from: string | null, to: string | null) => void;
  showPendingOnly: boolean;
  onShowPendingOnlyChange: (value: boolean) => void;
  onResetFilters: () => void;
  branchOptions: { value: string; label: string }[];
}

export function RepositoryManagementFilters({
  selectedProvider,
  onProviderChange,
  selectedBranch,
  onBranchChange,
  selectedStatus,
  onStatusChange,
  lastSyncedFrom,
  lastSyncedTo,
  onLastSyncedChange,
  showPendingOnly,
  onShowPendingOnlyChange,
  onResetFilters,
  branchOptions,
}: RepositoryManagementFiltersProps) {
  const providerOptions = [
    { value: 'GITHUB', label: 'GitHub' },
    { value: 'GITLAB', label: 'GitLab' },
    { value: 'BITBUCKET', label: 'Bitbucket' },
  ];

  const statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'SYNCING', label: 'Syncing' },
    { value: 'ERROR', label: 'Error' },
  ];

  const hasActiveFilters =
    selectedProvider.length > 0 ||
    selectedBranch.length > 0 ||
    selectedStatus.length > 0 ||
    lastSyncedFrom !== null ||
    lastSyncedTo !== null ||
    showPendingOnly;

  const handleDateChange = (range: DateRange) => {
    if (range.startDate && range.endDate) {
      onLastSyncedChange(
        range.startDate.toISOString(),
        range.endDate.toISOString()
      );
    } else {
      onLastSyncedChange(null, null);
    }
  };

  const initialDateRange = useMemo((): DateRange => {
    if (lastSyncedFrom && lastSyncedTo) {
      return {
        startDate: new Date(lastSyncedFrom),
        endDate: new Date(lastSyncedTo),
      };
    }
    return { startDate: null, endDate: null };
  }, [lastSyncedFrom, lastSyncedTo]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center gap-4 w-full">
        <AppliedFilters />
        <FilterDropdown
          onSelectionChange={(values) =>
            onProviderChange(values as RepositoryProvider[])
          }
          options={providerOptions}
          placeholder="Provider"
          selectedValues={selectedProvider}
          className="w-full"
        />
        <FilterDropdown
          onSelectionChange={(values) => onBranchChange(values as string[])}
          options={branchOptions}
          placeholder="Branch"
          selectedValues={selectedBranch}
          className="w-full"
        />
        <FilterDropdown
          onSelectionChange={(values) =>
            onStatusChange(values as RepositoryStatus[])
          }
          options={statusOptions}
          placeholder="Status"
          selectedValues={selectedStatus}
          className="w-full"
        />
        <DatePicker
          placeholder="Last Synced"
          className="w-full"
          initialRange={initialDateRange}
          onDateRangeChange={handleDateChange}
        />
        <TextButton
          disabled={!hasActiveFilters}
          className="w-[94px] text-nowrap"
          size="xs"
          onClick={onResetFilters}
        >
          Reset Filters
        </TextButton>
      </div>
      <Toggle
        checked={showPendingOnly}
        onChange={onShowPendingOnlyChange}
        label="Only show pending authentication"
      />
    </div>
  );
}
